package com.sddamico.draftmachine.pages.pools

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.key
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.sddamico.draftmachine.api.ApiException
import com.sddamico.draftmachine.api.apiGet
import com.sddamico.draftmachine.api.apiJson
import com.sddamico.draftmachine.api.apiPost
import com.sddamico.draftmachine.components.layouts.LoadingFrame
import com.sddamico.draftmachine.components.layouts.LoadingSection
import com.sddamico.draftmachine.components.layouts.MobileFrame
import com.sddamico.draftmachine.components.sections.AppHeader
import com.sddamico.draftmachine.components.sections.AppNav
import com.sddamico.draftmachine.components.widgets.SegmentedTabs
import com.sddamico.draftmachine.data.Sport
import com.sddamico.draftmachine.ds.DmColor
import com.sddamico.draftmachine.ds.DmType
import com.sddamico.draftmachine.ds.core.Avatar
import com.sddamico.draftmachine.ds.core.AvatarSize
import com.sddamico.draftmachine.ds.core.Badge
import com.sddamico.draftmachine.ds.core.BadgeTone
import com.sddamico.draftmachine.ds.core.Tag
import com.sddamico.draftmachine.ds.displayFlex
import com.sddamico.draftmachine.ds.feedback.EmptyState
import com.sddamico.draftmachine.ds.forms.Button
import com.sddamico.draftmachine.ds.forms.ButtonSize
import com.sddamico.draftmachine.ds.forms.ButtonVariant
import com.sddamico.draftmachine.ds.forms.IconButton
import com.sddamico.draftmachine.ds.forms.Input
import com.sddamico.draftmachine.ds.motion.Motion
import com.sddamico.draftmachine.ds.motion.ViewTransitions
import com.sddamico.draftmachine.models.PlayerPoolResponse
import com.sddamico.draftmachine.models.ClonePoolRequest
import com.sddamico.draftmachine.util.LoadState
import com.sddamico.draftmachine.util.UNREACHABLE_MESSAGE
import com.sddamico.draftmachine.util.rememberAuthState
import com.sddamico.draftmachine.util.sportLabel
import com.sddamico.draftmachine.util.transitionTo
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch
import kotlinx.serialization.builtins.ListSerializer
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div

private val TABS = listOf("Official", "Community", "Yours")
private val TAB_PARAMS = listOf("official", "community", "mine")

/** null = All sports; Sport = that sport; the sentinel MIXED entry filters sport == null pools. */
private data class SportChip(val label: String, val sport: Sport?, val mixed: Boolean = false)

private val SPORT_CHIPS = listOf(
  SportChip("All sports", null),
  SportChip("NFL", Sport.NFL),
  SportChip("MLB", Sport.MLB),
  SportChip("NBA", Sport.NBA),
  SportChip("NHL", Sport.NHL),
  SportChip("NCAA FB", Sport.NCAA_FOOTBALL),
  SportChip("NCAA BB", Sport.NCAA_BASKETBALL),
  SportChip("Golf", Sport.GOLF),
  SportChip("Tennis (M)", Sport.MENS_TENNIS),
  SportChip("Tennis (W)", Sport.WOMENS_TENNIS),
  SportChip("Other", Sport.OTHER),
  SportChip("Mixed", null, mixed = true),
)

/**
 * The pools index (frames 1a–1b): Official / Community / Yours tabs, search, sport chips, and the
 * pool list. Community rows carry a forged-by byline and a clone shortcut.
 */
@Page
@Composable
fun PoolsPage() {
  val ctx = rememberPageContext()
  val auth = rememberAuthState()

  when (val s = auth.value) {
    is LoadState.Loading -> LoadingFrame()
    is LoadState.Failed -> { LaunchedEffect(Unit) { ctx.router.transitionTo("/login") } }
    is LoadState.Ready -> {
      val user = s.value
      if (user == null) {
        LaunchedEffect(Unit) { ctx.router.transitionTo("/login") }
      } else {
        PoolsIndex(email = user.email)
      }
    }
  }
}

@Composable
private fun PoolsIndex(email: String) {
  val ctx = rememberPageContext()
  val scope = rememberCoroutineScope()
  var tab by remember { mutableStateOf(0) }
  var search by remember { mutableStateOf("") }
  var chip by remember { mutableStateOf(0) }
  var pools by remember { mutableStateOf<LoadState<List<PlayerPoolResponse>>>(LoadState.Loading) }
  var cloneBusy by remember { mutableStateOf(false) }

  LaunchedEffect(tab) {
    pools = LoadState.Loading
    val loaded = try {
      LoadState.Ready(
        apiGet("/api/player-pools?tab=${TAB_PARAMS[tab]}", ListSerializer(PlayerPoolResponse.serializer()))
      )
    } catch (e: ApiException) {
      LoadState.Failed(e.errorMessage)
    } catch (e: Throwable) {
      LoadState.Failed(UNREACHABLE_MESSAGE)
    }
    ViewTransitions.run(Motion.Update) { pools = loaded }
  }

  fun clone(pool: PlayerPoolResponse) {
    if (cloneBusy) return
    cloneBusy = true
    scope.launch {
      try {
        val cloned = apiPost(
          "/api/player-pool-clone",
          apiJson.encodeToString(ClonePoolRequest.serializer(), ClonePoolRequest(pool.id)),
          PlayerPoolResponse.serializer(),
        )
        ctx.router.transitionTo("/pools/${cloned.id}")
      } catch (_: Throwable) {
        cloneBusy = false
      }
    }
  }

  MobileFrame(
    modifier = Modifier.displayFlex().flexDirection(FlexDirection.Column),
    header = { AppHeader(email = email, nav = AppNav.Pools) },
  ) {
    Div(
      attrs = Modifier.flex(1).displayFlex().flexDirection(FlexDirection.Column).gap(12.px)
        .padding(top = 22.px, leftRight = 16.px, bottom = 24.px).toAttrs()
    ) {
      Div(
        attrs = Modifier.displayFlex().alignItems(AlignItems.FlexEnd)
          .justifyContent(JustifyContent.SpaceBetween).gap(12.px).toAttrs()
      ) {
        SpanText(
          "Player pools",
          Modifier.fontFamily(*DmType.display).fontSize(28.px).lineHeight(1).color(DmColor.text),
        )
        Button("New pool", variant = ButtonVariant.Primary, icon = "plus", onClick = {
          ctx.router.transitionTo("/pools/new")
        })
      }
      SegmentedTabs(TABS, tab) { ViewTransitions.run(Motion.Update) { tab = it } }
      Input(
        placeholder = if (tab == 1) "Search community pools" else "Search pools",
        icon = "magnifying-glass",
        value = search,
        onValueChange = { search = it },
      )
      Div(
        attrs = Modifier.displayFlex().gap(6.px).padding(bottom = 2.px)
          .styleModifier { property("overflow-x", "auto") }.toAttrs()
      ) {
        SPORT_CHIPS.forEachIndexed { i, c ->
          Tag(
            label = c.label,
            icon = if (c.mixed) "stack" else c.sport?.let(::sportIcon),
            active = i == chip,
            onClick = { ViewTransitions.run(Motion.Update) { chip = i } },
            modifier = Modifier.flex("0 0 auto"),
          )
        }
      }
      when (val p = pools) {
        is LoadState.Loading -> LoadingSection()
        is LoadState.Failed -> EmptyState(icon = "plugs", title = "Couldn't load pools", message = p.message)
        is LoadState.Ready -> PoolList(
          pools = filterPools(p.value, search, SPORT_CHIPS[chip]),
          tab = tab,
          onOpen = { ctx.router.transitionTo("/pools/${it.id}") },
          onClone = ::clone,
        )
      }
    }
  }
}

private fun filterPools(pools: List<PlayerPoolResponse>, search: String, chip: SportChip): List<PlayerPoolResponse> {
  val q = search.trim().lowercase()
  return pools.filter { p ->
    (q.isEmpty() || p.name.lowercase().contains(q)) &&
      when {
        chip.mixed -> p.sport == null
        chip.sport != null -> p.sport == chip.sport
        else -> true
      }
  }
}

@Composable
private fun PoolList(
  pools: List<PlayerPoolResponse>,
  tab: Int,
  onOpen: (PlayerPoolResponse) -> Unit,
  onClone: (PlayerPoolResponse) -> Unit,
) {
  if (pools.isEmpty()) {
    val (title, message) = when (tab) {
      0 -> "No official pools yet" to "The machine's stock shelves are being filled."
      1 -> "No community pools yet" to "Forge one and list it for the community."
      else -> "No pools of yours yet" to "Forge your first pool and it'll rack up here."
    }
    EmptyState(icon = "rows", title = title, message = message)
    return
  }
  Div(attrs = Modifier.displayFlex().flexDirection(FlexDirection.Column).gap(8.px).toAttrs()) {
    pools.forEach { pool ->
      key(pool.id) {
        PoolRow(
          pool = pool,
          onClick = { onOpen(pool) },
          byline = if (tab == 1 && pool.creatorUsername != null) {
            {
              Div(attrs = Modifier.displayFlex().alignItems(AlignItems.Center).gap(6.px).margin(top = 5.px).toAttrs()) {
                Avatar(name = pool.creatorUsername!!, size = AvatarSize.Xs)
                SpanText(
                  "BY @${pool.creatorUsername!!}",
                  Modifier.fontFamily(*DmType.mono).fontSize(11.px).color(DmColor.textSecondary),
                )
              }
            }
          } else null,
          trailing = {
            when {
              pool.isOfficial -> Badge("Official", tone = BadgeTone.Brass, icon = "seal-check")
              tab == 1 -> Div(
                attrs = Modifier.displayFlex().toAttrs {
                  onClick { event -> event.stopPropagation() }
                }
              ) {
                IconButton(
                  icon = "copy", label = "Clone this pool", variant = ButtonVariant.Ghost,
                  size = ButtonSize.Sm, onClick = { onClone(pool) },
                )
              }
            }
          },
        )
      }
    }
  }
}
